// Meeting Log — the archive of everything captured in review.
//
// Two streams in one timeline: free meeting notes (captured here) and the
// per-KPI weekly notes logged on the Command Centre. This is the "archived to
// the audit log" surface from the build brief — every contribution stays
// visible, in order, attributed, so accountability doesn't evaporate between
// meetings (the Nonso brief).

import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../components/AuthGate";
import { KPI_BUNDLE } from "../lib/kpis";
import { addNote, listNotes, notesAreShared, type Note } from "../lib/notes";

const KPI_TITLE: Record<string, string> = Object.fromEntries(
  KPI_BUNDLE.kpis.map((k) => [k.id, `${k.code} · ${k.title}`]),
);

export function MeetingLogPage() {
  const { session } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [draft, setDraft] = useState("");

  const refresh = useCallback(async () => {
    setNotes(await listNotes());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submit = useCallback(async () => {
    if (!draft.trim()) return;
    await addNote({
      kind: "meeting",
      kpiId: null,
      author: session?.email ?? "unknown",
      text: draft,
    });
    setDraft("");
    await refresh();
  }, [draft, session, refresh]);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__meta">
            ACCOUNTABILITY · MEETING CAPTURE {!notesAreShared && "· local only (worker not deployed)"}
          </div>
          <h1>Meeting Log</h1>
        </div>
      </div>

      <p style={lede}>
        Every meeting produces an artefact. Capture decisions, questions and disagreements here;
        per-KPI notes logged on the Command Centre flow into the same record. Newest first.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 22, maxWidth: 820 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit();
          }}
          placeholder="Capture a decision, a question, a disagreement… (⌘/Ctrl + Enter)"
          style={input}
        />
        <button onClick={() => void submit()} disabled={!draft.trim()} style={btn}>
          Log
        </button>
      </div>

      {notes.length === 0 ? (
        <div className="empty">No notes captured yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 820 }}>
          {notes.map((n) => (
            <div key={n.id} className="card" style={{ padding: "14px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={kindTag(n.kind)}>
                  {n.kind === "kpi" ? KPI_TITLE[n.kpiId ?? ""] ?? "KPI" : "MEETING"}
                </span>
                <span style={{ color: "var(--teal)", fontWeight: 700, fontSize: 11 }}>
                  {n.author}
                </span>
                <span style={{ color: "var(--ink-faint)", fontSize: 10.5 }}>
                  {new Date(n.at).toLocaleString()}
                  {n.synced ? "" : " · local"}
                </span>
              </div>
              <div style={{ fontSize: 13.5, color: "var(--ink)", lineHeight: 1.55 }}>{n.text}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const lede: React.CSSProperties = {
  fontSize: 14,
  color: "var(--ink-dim)",
  marginBottom: 20,
  maxWidth: 820,
  lineHeight: 1.55,
};
const input: React.CSSProperties = {
  flex: 1,
  background: "var(--bg-1)",
  border: "1px solid var(--edge)",
  borderRadius: "var(--r-sm)",
  padding: "10px 12px",
  color: "var(--ink)",
  fontSize: 13,
  fontFamily: "var(--font-display)",
  outline: "none",
};
const btn: React.CSSProperties = {
  background: "linear-gradient(135deg, var(--mint), var(--teal-deep))",
  color: "var(--bg-0)",
  border: "none",
  borderRadius: "var(--r-sm)",
  padding: "10px 18px",
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  cursor: "pointer",
};
function kindTag(kind: string): React.CSSProperties {
  const c = kind === "kpi" ? "var(--mint)" : "var(--amber)";
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 8.5,
    letterSpacing: "0.12em",
    fontWeight: 700,
    color: c,
    border: `1px solid ${c}`,
    borderRadius: 5,
    padding: "2px 6px",
  };
}
